import cpeHistoryService from '../services/cpeHistoryService';
import type { EducationCategory } from '../services/cpeHistoryService';
import { formatDeliveryMethods } from './deliveryMethods';

// Shared display + data helpers for credit-limit bases. A limit caps either
// delivery methods or fields of study (education categories), never both —
// enforced server-side; the basis is derived from which array is non-empty.

/** The subset of CreditLimitDto/CreditLimitFulfillmentDto the caption needs. */
interface CreditLimitBasisFields {
  deliveryMethods: string[];
  educationCategoryNames: string[];
}

/**
 * Caption for a limit's basis: delivery-method labels for delivery limits
 * (e.g. "Self-Study, Nano Learning"), category names for field-of-study limits
 * (e.g. "Taxes, Auditing"). Never call formatDeliveryMethods directly on a
 * limit — field rows store an empty deliveryMethods array.
 */
export function formatCreditLimitBasis(limit: CreditLimitBasisFields): string {
  if (limit.educationCategoryNames.length > 0) {
    return limit.educationCategoryNames.join(', ');
  }
  return formatDeliveryMethods(limit.deliveryMethods);
}

// Module-level memo: education categories are global seed data (id + name),
// so one fetch per page load is plenty — no TTL, no user keying.
let cachedCategories: Promise<EducationCategory[]> | null = null;

export function getEducationCategoriesMemo(): Promise<EducationCategory[]> {
  if (!cachedCategories) {
    const promise = cpeHistoryService.getEducationCategories();
    cachedCategories = promise;
    // Don't cache failures — the next mount should retry.
    promise.catch(() => {
      if (cachedCategories === promise) cachedCategories = null;
    });
  }
  return cachedCategories;
}

/** Test hook: clears the module-level category cache. */
export function clearEducationCategoriesCacheForTests(): void {
  cachedCategories = null;
}
